import React from 'react'
import cn from 'classnames'
import { useDropdown } from '@hooks/useDropdown'
import { ChevronDownIcon } from '@components/icons/ChevronDownIcon'
import { Option } from '@interfaces/commons/Option'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'

interface SelectInputProps {
  value: string
  label?: string
  className?: string
  onChange: (value: any) => void
  options: Option[]
}

const SelectInput = ({
  value,
  className,
  onChange,
  label,
  options,
}: SelectInputProps) => {
  const { isLightTheme, isSepiaTheme, isNightTheme, isDarkTheme } = useTheme()
  const { dropdownRef, isShowDropdown, toggleDropdown } = useDropdown()

  return (
    <div className={cn('relative select-none', className)} ref={dropdownRef}>
      {label && (
        <div
          className={cn(
            'text-[12px] text-secondary-1 dark:text-dark-10 font-light mb-[4px]'
          )}
        >
          {label}
        </div>
      )}
      <div
        className={cn(
          'w-full h-[40px] border-[1px] rounded-[8px] text-[14px] font-medium p-[10px] cursor-pointer',
          {
            'border-primary': isShowDropdown,
            'border-gray-3 text-secondary': isLightTheme,
            'border-sepia-1 text-secondary': isSepiaTheme,
            'border-secondary-1 text-white': isNightTheme,
            'border-gray-16 text-gray-16': isDarkTheme,
          }
        )}
        onClick={toggleDropdown}
      >
        <div className='flex items-center justify-between font-light'>
          <div className='flex space-x-[5px]'>
            {options?.find(row => row.value === value)?.label || label}
          </div>
          <ChevronDownIcon className='w-[16px] h-[16px] text-primary' />
        </div>
        <div
          className={cn(
            'absolute w-full z-10 mt-[12px] right-0 border rounded-[10px] max-h-[400px] overflow-y-auto shadow-[0_3px_12px_0_rgba(51,51,51,0.05)]',
            {
              hidden: !isShowDropdown,
              'bg-white border-gray-3': isLightTheme,
              'bg-sepia border-sepia-1': isSepiaTheme,
              'bg-dark border-secondary-1': isNightTheme,
              'bg-dark-5 border-gray-16': isDarkTheme,
            }
          )}
        >
          <div className='my-[10px]'>
            {options.map((option, index) => (
              <div
                className={cn(
                  'font-light py-[6px] px-[15px] hover:text-primary',
                  {
                    'hover:bg-primary-1': isLightTheme,
                    'hover:bg-sepia-3': isSepiaTheme,
                    'hover:bg-dark-6': isNightTheme,
                    'hover:bg-dark-7': isDarkTheme,
                  }
                )}
                key={index}
                onClick={() => onChange(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectInput
